// A picture a teacher picked, made into the studio's logo in the browser
// (#498): opened, fitted into the core's box, and encoded as a PNG or JPEG
// data URI small enough to keep on the studio document.
//
// Which encodings to try, in which order and at what size, is the core's
// (`logoPlan`, core/src/logo.rs), and so is what counts as a logo at the end
// (`normaliseLogo`) — the phones run the same plan over their own image
// libraries. What is here is the browser's half: a canvas to draw on.
//
// `codec` is a parameter so the plan can be walked under jsdom, which has no
// canvas: the ui tests hand in a fake that reports sizes and returns fixed
// data URIs, and assert what was asked of it.
import { LOGO_WORDING, logoPlan, logoSourceRefusal, normaliseLogo } from '../domain/logo';

export class LogoError extends Error {
  constructor(code) {
    super(LOGO_WORDING[code] || LOGO_WORDING.unreadable);
    this.name = 'LogoError';
    this.code = code;
  }
}

// ── The browser's decoder and encoder ─────────────────────────────────────

async function decodeWithImage(file) {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
  return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
}

export const browserCodec = {
  // `createImageBitmap` where there is one, because it decodes off the main
  // thread and applies a photograph's EXIF rotation; an <img> otherwise.
  async decode(file) {
    if (typeof createImageBitmap === 'function') {
      try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close?.() };
      } catch {
        // Some browsers refuse the options bag or a format the <img> path
        // still reads; fall through rather than call it unreadable.
      }
    }
    return decodeWithImage(file);
  },

  // One step of the plan. A JPEG has no transparency, so it is drawn over
  // white — the paper a logo is printed on — rather than the black a canvas
  // would otherwise leave under a transparent pixel.
  async encode(picture, { format, quality, width, height }) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    if (format === 'jpeg') {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(picture.source, 0, 0, width, height);
    return canvas.toDataURL(format === 'jpeg' ? 'image/jpeg' : 'image/png', quality);
  },
};

// ── The plan, walked ───────────────────────────────────────────────────────

// The logo made from `file`, as a data URI the core keeps, or a thrown
// LogoError whose message is the sentence to show.
export async function makeLogo(file, codec = browserCodec) {
  const refusal = logoSourceRefusal(file?.type || null, file?.size ?? 0);
  if (refusal) throw new LogoError(refusal);

  let picture;
  try {
    picture = await codec.decode(file);
  } catch {
    throw new LogoError('unreadable');
  }
  try {
    const steps = logoPlan(picture.width || 0, picture.height || 0, file?.type || null);
    if (steps.length === 0) throw new LogoError('unreadable');
    for (const step of steps) {
      // In order, one at a time, stopping at the first that fits: each step
      // is smaller than the last, and most pictures fit at the first.
      const uri = await codec.encode(picture, step);
      const kept = uri ? normaliseLogo(uri) : null;
      if (kept) return kept;
    }
    throw new LogoError('too_detailed');
  } finally {
    picture?.close?.();
  }
}
